import { AdminLoader } from './AdminLoader';
import { ErrorBoundary } from './ErrorBoundary';
import { cn } from '@exam-prep/ui';
import { useEffect, useId, type ReactNode, Suspense } from 'react';
import { useAuth } from '../lib/auth';
import { buildNav, pageTitle } from '../lib/nav';
import { useNavBadges } from '../lib/nav-badges';
import { useNavDrawer, useSidebarCollapsed } from '../lib/sidebar';
import { useTheme } from '../lib/theme';
import { DesktopSidebar, MobileTopBar, NavDrawer } from './NavShell';

/**
 * Admin shell: a persistent left sidebar on wide screens (collapsible to an icon rail), and a top
 * bar with an off-canvas drawer below 1024px. The page title row sits at the top of the content.
 */
export function Layout({ route, children }: { route: string[]; children: ReactNode }) {
  const { me, logout } = useAuth();
  const [theme, setTheme] = useTheme();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const routeKey = route.join('/');
  const { drawer, open, close, drawerRef, menuButton } = useNavDrawer(routeKey);
  // A new page starts at the top: arriving scrolled down from a long list showed an empty area.
  const pageKey = route.slice(0, 2).join('/');
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pageKey]);
  const navId = useId();
  const badges = useNavBadges(me);

  const { groups, account } = buildNav({
    me,
    route,
    products: badges.products,
    reviewOpen: badges.reviewOpen,
    supportOpen: badges.supportOpen,
  });
  const title = pageTitle(route);
  // A page's name appears once: the sidebar item is the name, so the visible title row is only
  // for pages the sidebar does not name (a single question, a source, a new exam, ...). A
  // product's own name is shown by the product page itself.
  const activeLabel = [...groups.flatMap((g) => g.items), account].find((i) => i.active)?.label;
  const showTitle =
    activeLabel?.toLowerCase() !== title.toLowerCase() && !(route[0] === 'products' && route[1]);

  const sidebar = {
    groups,
    account,
    email: me?.user.email ?? '',
    roles: me?.roles.map((r) => r.role.replace(/_/g, ' ')).join(', ') || 'no role',
    theme,
    onTheme: setTheme,
    onSignOut: () => void logout(),
  };
  return (
    <div className="min-h-screen">
      <a
        href="#admin-main"
        className="brand-skip"
        onClick={(e) => {
          // Hash routing: focus the main region instead of changing the route.
          e.preventDefault();
          document.getElementById('admin-main')?.focus();
        }}
      >
        Skip to content
      </a>

      {/* Desktop: persistent sidebar / icon rail. */}
      <DesktopSidebar
        sidebar={sidebar}
        navId={navId}
        collapsed={collapsed}
        toggleCollapsed={toggleCollapsed}
      />

      {/* Narrow screens: top bar + off-canvas drawer. */}
      <MobileTopBar
        title={title}
        navId={navId}
        open={drawer}
        onOpen={open}
        menuButtonRef={menuButton}
      />
      {drawer ? (
        <NavDrawer sidebar={sidebar} navId={navId} onClose={close} drawerRef={drawerRef} />
      ) : null}

      <div
        inert={drawer}
        className={cn(
          'min-w-0 transition-[padding] duration-200 motion-reduce:transition-none',
          collapsed ? 'lg:pl-[76px]' : 'lg:pl-60',
        )}
      >
        <main
          id="admin-main"
          tabIndex={-1}
          className="mx-auto flex min-w-0 max-w-[1600px] flex-col gap-4 px-4 pt-4 pb-10 outline-none lg:px-8 lg:pt-6"
        >
          {showTitle ? (
            <div className="flex flex-wrap items-end justify-between gap-2 border-b pb-3">
              <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
            </div>
          ) : (
            <h1 className="sr-only">{title}</h1>
          )}
          <div key={routeKey} className="admin-page flex min-w-0 flex-col gap-4">
            {/* Keyed by route: a crash or failed chunk on one page never blanks the next. */}
            <ErrorBoundary>
              <Suspense fallback={<AdminLoader />}>{children}</Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
